/**
 * useQuickAnswerConversation — resolves the single WlbHelpAgent ("Ask
 * anything!") conversation, creating it on first use, then loads its details.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import conversationApi from '../api/conversation/conversationApi';
import messageService from '../api/services/messageService';
import type { ConversationDto } from '../api/conversation/conversationTypes';

export interface UseQuickAnswerConversationResult {
  conversationId: number | null;
  conversationDetails: ConversationDto | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

const extractConversationIdFromList = (data: unknown): number | null => {
  if (!data) return null;

  if (Array.isArray(data)) {
    const first = data[0] as ConversationDto | undefined;
    return first?.conversationId ?? null;
  }

  const envelope = data as Record<string, unknown>;
  const list =
    (envelope.conversations as ConversationDto[] | undefined) ??
    (envelope.items as ConversationDto[] | undefined) ??
    (envelope.data as ConversationDto[] | undefined);

  if (Array.isArray(list) && list.length > 0) {
    return (list[0] as ConversationDto).conversationId ?? null;
  }

  return null;
};

const useQuickAnswerConversation = (
  active: boolean,
): UseQuickAnswerConversationResult => {
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversationDetails, setConversationDetails] =
    useState<ConversationDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [retryCount, setRetryCount] = useState(0);
  const resolveInProgress = useRef(false);

  useEffect(() => {
    if (!active) return;
    if (resolveInProgress.current) return;
    resolveInProgress.current = true;

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      setError(null);

      try {
        let resolvedId: number | null = null;

        const listRes = await conversationApi.listWithFilter({
          pageNumber: 1,
          pageSize: 20,
          searchTerm: '',
          filter: 'WlbHelpAgent',
        });
        const idFromList = extractConversationIdFromList(listRes.data);
        if (idFromList) {
          resolvedId = idFromList;
        }

        if (!resolvedId) {
          const dto = await messageService.initiateAiAgentConversation({
            conversationType: 'WlbHelpAgent',
          });
          const idFromInitiate = dto?.conversationId ?? null;
          if (idFromInitiate) {
            resolvedId = idFromInitiate;
          }
        }

        if (cancelled) return;

        if (!resolvedId) {
          setError('Could not initialize Quick Answer conversation.');
          setLoading(false);
          return;
        }

        setConversationId(resolvedId);

        const detailsRes = await conversationApi.getById(resolvedId);
        if (cancelled) return;

        setConversationDetails(detailsRes.data ?? null);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Quick Answer conversation.',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        resolveInProgress.current = false;
      }
    };

    resolve();

    return () => {
      cancelled = true;
      resolveInProgress.current = false;
    };
  }, [active, retryCount]);

  const retry = useCallback(() => {
    setConversationId(null);
    setConversationDetails(null);
    setError(null);
    resolveInProgress.current = false;
    setRetryCount(c => c + 1);
  }, []);

  return { conversationId, conversationDetails, loading, error, retry };
};

export default useQuickAnswerConversation;
